import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Box, HoverCard, Popover, Portal, Text} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import {CloseIcon, InfoIconFilled} from '../icons'

const HelpContent = ({helpText, helpList}) => {
    const content =
        typeof helpText === 'string' ? (
            <Box dangerouslySetInnerHTML={{__html: helpText}} />
        ) : (
            helpText
        )

    return (
        <>
            {helpText && <Text as="div">{content}</Text>}
            {helpList.length > 0 && (
                <Box as="ul">
                    {helpList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Box>
            )}
        </>
    )
}

HelpContent.propTypes = {
    helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    helpList: PropTypes.arrayOf(PropTypes.node).isRequired
}

export const InfoIconTooltip = ({
    id,
    name,
    helpText,
    helpList = [],
    heading,
    showClose = false,
    icon: Icon = InfoIconFilled,
    iconProps = {},
    variant,
    size = 'full'
}) => {
    const generatedInfoIconId = useId()
    const infoIconId = id || generatedInfoIconId
    const popoverId = useId()
    const hoverCardId = useId()
    const {formatMessage} = useIntl()

    const renderContent = (Overlay, setOpen, isPopover) => (
        <Portal>
            <Overlay.Positioner>
                <Overlay.Content role="tooltip" data-show-close={showClose}>
                    <Overlay.Arrow />

                    {showClose &&
                        (isPopover ? (
                            <Popover.CloseTrigger
                                aria-label={formatMessage({
                                    id: 'global.close',
                                    defaultMessage: 'Close'
                                })}
                            >
                                <CloseIcon viewBox="0 0 16 16" aria-hidden="true" />
                            </Popover.CloseTrigger>
                        ) : (
                            <Box
                                as="button"
                                type="button"
                                data-part="close-trigger"
                                aria-label={formatMessage({
                                    id: 'global.close',
                                    defaultMessage: 'Close'
                                })}
                                onClick={() => setOpen(false)}
                            >
                                <CloseIcon viewBox="0 0 16 16" aria-hidden="true" />
                            </Box>
                        ))}

                    {heading &&
                        (isPopover ? (
                            <Popover.Header data-show-close={showClose}>{heading}</Popover.Header>
                        ) : (
                            <Box as="div" data-part="header">
                                {heading}
                            </Box>
                        ))}

                    {isPopover ? (
                        <Popover.Body data-show-close={showClose && !heading}>
                            <HelpContent helpText={helpText} helpList={helpList} />
                        </Popover.Body>
                    ) : (
                        <Box as="div" data-part="body">
                            <HelpContent helpText={helpText} helpList={helpList} />
                        </Box>
                    )}
                </Overlay.Content>
            </Overlay.Positioner>
        </Portal>
    )

    const renderTrigger = (Overlay, contentId, overlayName) => (
        <Overlay.Trigger asChild>
            <Box
                as="button"
                type="button"
                aria-label={formatMessage({id: 'global.help', defaultMessage: 'Help'})}
                aria-describedby={infoIconId}
                aria-controls={contentId}
                data-testid={`${name}-${overlayName}-tooltip-testid`}
            >
                <Icon variant="tooltip" role="presentation" {...iconProps} />
            </Box>
        </Overlay.Trigger>
    )

    return (
        <>
            <Box as="div" id={infoIconId} hidden>
                <HelpContent helpText={helpText} helpList={helpList} />
            </Box>

            <Popover.Root
                lazyMount
                ids={{content: popoverId}}
                triggerMode="responsive"
                variant={variant}
                size={size}
                positioning={{placement: 'top'}}
            >
                {renderTrigger(Popover, popoverId, 'popover')}
                <Popover.Context>
                    {({setOpen}) => renderContent(Popover, setOpen, true)}
                </Popover.Context>
            </Popover.Root>

            <HoverCard.Root
                lazyMount
                ids={{content: hoverCardId}}
                triggerMode="responsive"
                variant={variant}
                size={size}
                positioning={{placement: 'top'}}
                openDelay={0}
                closeDelay={0}
            >
                {renderTrigger(HoverCard, hoverCardId, 'hover-card')}
                <HoverCard.Context>
                    {({setOpen}) => renderContent(HoverCard, setOpen, false)}
                </HoverCard.Context>
            </HoverCard.Root>
        </>
    )
}

InfoIconTooltip.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    helpList: PropTypes.arrayOf(PropTypes.node),
    heading: PropTypes.node,
    showClose: PropTypes.bool,
    icon: PropTypes.elementType,
    iconProps: PropTypes.object,
    variant: PropTypes.string,
    size: PropTypes.oneOf(['compact', 'wide', 'full'])
}
